"use client";
import { motion } from "framer-motion";

const About = () => {
	return (
		<div className="bg-white pt-20">
			{/* Hero Section */}
			<section className="relative py-20 bg-[#819f3d] text-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.h1
						initial={{ y: -20, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						transition={{ duration: 0.8 }}
						className="text-4xl md:text-5xl font-bold text-center mb-6">
						Who We Are
					</motion.h1>
				</div>
			</section>

			{/* Brief History */}
			<section className="py-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="bg-white p-8 rounded-lg shadow-lg">
						<h2 className="text-2xl font-bold text-[#1666ba] mb-4">
							Our History
						</h2>
						<p className="text-gray-600">
							The School of Leadership and Diplomacy (SLD) was established with the 
							mission to inspire and prepare emerging leaders to navigate the complexities 
							of the modern world through knowledge, dialogue, and diplomacy. Since its 
							founded, the school has evolved into an international platform dedicated 
							to advancing education in leadership, global affairs, and strategic communication. 
							Over the years, SLD has organized impactful programs, conferences, and training 
							sessions in various countries, creating opportunities for youth and professionals 
							to engage in meaningful discussions on global challenges. By integrating leadership 
							principles with diplomatic skills, the School of Leadership and Diplomacy continues 
							to shape future changemakers committed to peace, collaboration, and sustainable global 
							progress.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Mission & Vision Section */}
			<section className="py-16 bg-gray-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid md:grid-cols-2 gap-8">
						<motion.div
							initial={{ y: 50, opacity: 0 }}
							whileInView={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.8 }}
							className="bg-white p-8 rounded-lg shadow-lg">
							<h2 className="text-2xl font-bold text-[#1666ba] mb-4">
								Our Mission
							</h2>
							<p className="text-gray-600">
								Our mission is to empower emerging leaders with the knowledge, skills, and values required 
								to lead with integrity, empathy, and global vision. The School of Leadership and Diplomacy 
								strives to create a learning environment that fosters critical thinking, intercultural dialogue, 
								and responsible decision-making. Through experiential learning, leadership training, and diplomatic 
								engagement, we aim to develop individuals who can effectively address global challenges, promote 
								peace, and contribute to building a more just and inclusive world.
							</p>
						</motion.div>
						<motion.div
							initial={{ y: 50, opacity: 0 }}
							whileInView={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							className="bg-white p-8 rounded-lg shadow-lg">
							<h2 className="text-2xl font-bold text-[#1666ba] mb-4">
								Our Vision
							</h2>
							<p className="text-gray-600">
								Our vision is to build a world where leadership is defined by wisdom, compassion, 
								and collaboration. The School of Leadership and Diplomacy envisions a global community 
								of leaders who champion dialogue over division and innovation over indifference. We 
								aspire to become a center of excellence in leadership and diplomatic education—where 
								diverse minds unite to create sustainable solutions, advance human rights, and foster 
								peaceful international cooperation for future generations.
							</p>
						</motion.div>
					</div>
				</div>
			</section>

			{/* Core Values Section */}
			<section className="py-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.h2
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						transition={{ duration: 0.8 }}
						className="text-3xl font-bold text-center text-[#1666ba] mb-12">
						Our Core Values
					</motion.h2>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
						{[
							{
								title: "Integrity",
								description:
									"Maintaining the highest standards of ethical conduct and professional excellence",
								icon: "⭐",
							},
							{
								title: "Innovation",
								description:
									"Embracing new ideas and approaches to address global challenges",
								icon: "💡",
							},
							{
								title: "Collaboration",
								description:
									"Fostering partnerships and teamwork across cultures and borders",
								icon: "🤝",
							},
							{
								title: "Global Perspective",
								description:
									"Understanding and respecting diverse viewpoints and cultures",
								icon: "🌍",
							},
						].map((value, index) => (
							<motion.div
								key={index}
								initial={{ y: 50, opacity: 0 }}
								whileInView={{ y: 0, opacity: 1 }}
								transition={{ duration: 0.8, delay: index * 0.2 }}
								className="text-center p-6 bg-white rounded-lg shadow-lg">
								<div className="text-4xl mb-4">{value.icon}</div>
								<h3 className="text-xl font-semibold text-[#1666ba] mb-2">
									{value.title}
								</h3>
								<p className="text-gray-600">{value.description}</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
};

export default About;
